import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@dashboard/lib/utils';

const Item = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-3 rounded px-3 py-2 text-sm', className)}
        {...props}
      />
    );
  },
);
Item.displayName = 'Item';

const ItemIcon = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'text-gray-500 dark:text-muted-foreground flex size-5 shrink-0 items-center justify-center [&>svg]:size-4',
          className,
        )}
        {...props}
      />
    );
  },
);
ItemIcon.displayName = 'ItemIcon';

const ItemMedia = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex shrink-0 items-center justify-center', className)}
        {...props}
      />
    );
  },
);
ItemMedia.displayName = 'ItemMedia';

const ItemContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex min-w-0 flex-1 flex-col text-left', className)}
        {...props}
      />
    );
  },
);
ItemContent.displayName = 'ItemContent';

const ItemTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('truncate font-medium', className)} {...props} />;
  },
);
ItemTitle.displayName = 'ItemTitle';

const ItemDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('text-gray-500 dark:text-muted-foreground truncate text-xs', className)}
        {...props}
      />
    );
  },
);
ItemDescription.displayName = 'ItemDescription';

const ItemAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'text-gray-400 dark:text-muted-foreground ml-auto flex shrink-0 items-center gap-1 [&>svg]:size-4',
          className,
        )}
        {...props}
      />
    );
  },
);
ItemAction.displayName = 'ItemAction';

interface ItemLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  asChild?: boolean;
}

const ItemLink = React.forwardRef<HTMLAnchorElement, ItemLinkProps>(
  ({ className, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';
    return (
      <Comp
        ref={ref}
        className={cn(
          'hover:bg-gray-50 dark:hover:bg-muted flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors cursor-pointer',
          className,
        )}
        {...props}
      />
    );
  },
);
ItemLink.displayName = 'ItemLink';

export { Item, ItemIcon, ItemMedia, ItemContent, ItemTitle, ItemDescription, ItemAction, ItemLink };
